import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal, Space, Table, Tag, Typography, Upload } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Button } from '@surecompanies/core_components'
import Papa from 'papaparse'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'

import {
  executeVcnCardBulk,
  isVcnBulkExecuteConflictError,
  previewVcnCardBulk,
} from '../../api/vcn-management-api-client'
import type {
  VcnCardBulkItem,
  VcnCardBulkPreviewResponse,
  VcnCardBulkPreviewRow,
} from '../../api/dto/vcn-card-bulk.dto'
import { VcnCardInUseStatus } from './types'
import { middleEllipsis } from './VcnManagementBulkUploadModal.helpers'
import {
  CARD_BULK_TEMPLATE_CSV,
  parseCardBulkRows,
} from './vcnManagementCardBulkUpload.helpers'
import {
  getStatusPillStyle,
  getVcnCardInUseLabel,
  getVcnCardInUseTone,
} from './vcnCardStatus'

const { Text } = Typography

type ModalPhase = 'idle' | 'preview_loading' | 'results'

/** Default view: the rows an admin has to make a decision about. */
type RowFilter = 'attention' | 'all' | 'orphan'

export interface VcnManagementCardBulkUploadModalProps {
  open: boolean
  onClose: () => void
  onJobStarted: () => void
}

function downloadTemplate(): void {
  const blob = new Blob([CARD_BULK_TEMPLATE_CSV], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'vcn-card-terminate-template.csv'
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000)
}

/** A row needs a decision when it will fail, or when terminating it costs a benefit its card. */
function needsAttention(row: VcnCardBulkPreviewRow): boolean {
  return !row.found || row.inUse === VcnCardInUseStatus.IN_USE
}

const columns: ColumnsType<VcnCardBulkPreviewRow> = [
  {
    title: 'Insured person',
    dataIndex: 'insuredPersonName',
    key: 'insuredPersonName',
    render: (value?: string) => <Text>{value ?? '—'}</Text>,
  },
  {
    title: 'Plan',
    dataIndex: 'inUse',
    key: 'inUse',
    width: 140,
    render: (_: unknown, row: VcnCardBulkPreviewRow) =>
      row.found && row.inUse ? (
        // The grid's own pill, not a lookalike: the preview and the screen must
        // agree about what the plan state looks like as well as what it means.
        <span style={getStatusPillStyle(getVcnCardInUseTone(row.inUse))}>
          {getVcnCardInUseLabel(row.inUse)}
        </span>
      ) : (
        <Tag color="error">Not found</Tag>
      ),
  },
  {
    title: 'Client',
    dataIndex: 'companyName',
    key: 'companyName',
    render: (value?: string) => <Text>{value ?? '—'}</Text>,
  },
  {
    title: 'Carrier',
    dataIndex: 'carrierName',
    key: 'carrierName',
    render: (value?: string) => <Text>{value ?? '—'}</Text>,
  },
  {
    title: 'Provider',
    dataIndex: 'provider',
    key: 'provider',
    width: 110,
  },
  {
    title: 'Last 4',
    dataIndex: 'lastFourDigits',
    key: 'lastFourDigits',
    width: 90,
    render: (value?: string) => <Text>{value ?? '—'}</Text>,
  },
  {
    title: 'VCN token (upload)',
    dataIndex: 'vcnToken',
    key: 'vcnToken',
    width: 220,
    render: (value: string) => (
      <Text copyable={{ text: value }} title={value}>
        {middleEllipsis(value)}
      </Text>
    ),
  },
]

/**
 * Bulk terminate from an uploaded file of `{provider, vcnToken}` rows.
 *
 * Separate from the benefit-keyed upload rather than a mode of it: that one
 * resolves each benefit's PRIMARY card, which is not the card a token names, and
 * it offers three actions where only terminate is card-keyed.
 *
 * The preview exists because this is the one place an admin cannot see what they
 * are acting on. With grid selection the rows are on screen; here there is a
 * filename. So the summary leads with the two numbers that change a decision —
 * how many rows will fail, and how many cards a live benefit is still using.
 */
export function VcnManagementCardBulkUploadModal({
  open,
  onClose,
  onJobStarted,
}: VcnManagementCardBulkUploadModalProps) {
  const previewRequestIdRef = useRef(0)
  const [phase, setPhase] = useState<ModalPhase>('idle')
  const [parseError, setParseError] = useState<string | null>(null)
  const [parseWarning, setParseWarning] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [items, setItems] = useState<VcnCardBulkItem[] | null>(null)
  const [preview, setPreview] = useState<VcnCardBulkPreviewResponse | null>(
    null
  )
  const [isExecuting, setIsExecuting] = useState(false)
  const [executeError, setExecuteError] = useState<string | null>(null)
  const [rowFilter, setRowFilter] = useState<RowFilter>('attention')

  const reset = useCallback(() => {
    previewRequestIdRef.current++
    setPhase('idle')
    setParseError(null)
    setParseWarning(null)
    setApiError(null)
    setItems(null)
    setPreview(null)
    setIsExecuting(false)
    setExecuteError(null)
    setRowFilter('attention')
  }, [])

  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const runPreview = useCallback(async (rows: VcnCardBulkItem[]) => {
    const requestId = ++previewRequestIdRef.current
    setPhase('preview_loading')
    setApiError(null)
    try {
      const response = await previewVcnCardBulk({ items: rows })
      // A second file picked while the first was in flight must not overwrite it.
      if (requestId !== previewRequestIdRef.current) return
      setPreview(response)
      setPhase('results')
    } catch {
      if (requestId !== previewRequestIdRef.current) return
      setApiError('Could not preview this file. Please try again.')
      setPhase('idle')
    }
  }, [])

  const handleFile = useCallback(
    (file: File) => {
      reset()
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.errors.length > 0) {
            setParseError(
              result.errors.map((err) => err.message).join('; ') ||
                'CSV parse error'
            )
            return
          }
          const parsed = parseCardBulkRows(result.data)
          const problems: string[] = []
          if (parsed.badProviderLines.length > 0) {
            problems.push(
              `Missing or unknown provider on row(s): ${parsed.badProviderLines.slice(0, 15).join(', ')}${parsed.badProviderLines.length > 15 ? '…' : ''}. Use WEX or MARQETA.`
            )
          }
          if (parsed.missingTokenLines.length > 0) {
            problems.push(
              `Missing VCN token on row(s): ${parsed.missingTokenLines.slice(0, 15).join(', ')}${parsed.missingTokenLines.length > 15 ? '…' : ''}.`
            )
          }
          if (parsed.duplicateCount > 0) {
            problems.push(
              `${parsed.duplicateCount} duplicate row(s) were ignored.`
            )
          }
          if (parsed.items.length === 0) {
            setParseError(
              problems.join(' ') ||
                'No rows found. The file needs a provider and a vcnToken column.'
            )
            return
          }
          // Rows were dropped but others survived: proceed and say what was left
          // out, rather than making the admin fix the file to see anything.
          if (problems.length > 0) setParseWarning(problems.join(' '))
          setItems(parsed.items)
          void runPreview(parsed.items)
        },
      })
      return false
    },
    [reset, runPreview]
  )

  const summary = preview?.summary
  const visibleRows = useMemo(() => {
    const rows = preview?.rows ?? []
    if (rowFilter === 'all') return rows
    if (rowFilter === 'orphan')
      return rows.filter(
        (row) => row.found && row.inUse !== VcnCardInUseStatus.IN_USE
      )
    return rows.filter(needsAttention)
  }, [preview, rowFilter])

  const attentionCount = useMemo(
    () => (preview?.rows ?? []).filter(needsAttention).length,
    [preview]
  )

  const handleRun = useCallback(async () => {
    if (!items?.length) return
    setIsExecuting(true)
    setExecuteError(null)
    try {
      // Every parsed row is sent, including the not-found ones: EPS resolves
      // again at run time, and a card that reappears between preview and run
      // should still be terminated.
      await executeVcnCardBulk({ items })
      onJobStarted()
      onClose()
    } catch (error) {
      setExecuteError(
        isVcnBulkExecuteConflictError(error)
          ? `Another bulk VCN job is already running (${error.jobId}). Wait for it to finish and try again.`
          : 'Could not start the job. Please try again.'
      )
    } finally {
      setIsExecuting(false)
    }
  }, [items, onClose, onJobStarted])

  return (
    <Modal
      title="Bulk terminate VCN cards (upload)"
      open={open}
      onCancel={onClose}
      width={980}
      destroyOnClose
      // Locked while the execute is out. Every other way of leaving this
      // screen — the X, the mask, Esc, Cancel, Upload another file — resets
      // the modal, and `reset` clears isExecuting: the admin could line up a
      // second file, and then the first request's onJobStarted/onClose would
      // dismiss the modal over it. The job that queued would be the file they
      // had already replaced.
      maskClosable={!isExecuting}
      closable={!isExecuting}
      keyboard={!isExecuting}
      footer={
        phase === 'results' ? (
          <Space wrap>
            <Button
              type="primary"
              danger
              loading={isExecuting}
              disabled={!items?.length}
              onClick={() => void handleRun()}
            >
              Terminate {items?.length ?? 0} card(s)
            </Button>
            <Button onClick={reset} disabled={isExecuting}>
              Upload another file
            </Button>
            <Button onClick={onClose} disabled={isExecuting}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose} disabled={phase === 'preview_loading'}>
            Cancel
          </Button>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleFile}
            disabled={isExecuting}
          >
            <Button icon={<UploadOutlined />} disabled={isExecuting}>
              Choose CSV
            </Button>
          </Upload>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            Download template
          </Button>
          <Text type="secondary">
            Two columns: provider and vcnToken. A Card Mode export works as-is.
          </Text>
        </Space>

        {parseError && <Alert type="error" showIcon message={parseError} />}
        {parseWarning && (
          <Alert type="warning" showIcon message={parseWarning} />
        )}
        {apiError && <Alert type="error" showIcon message={apiError} />}
        {executeError && <Alert type="error" showIcon message={executeError} />}

        {summary && (
          <>
            <Space wrap size="large">
              <Text strong>{summary.totalCount} rows</Text>
              <Text type="secondary">{summary.orphanCount} orphan</Text>
              <Text type={summary.inUseCount > 0 ? 'warning' : 'secondary'}>
                {summary.inUseCount} on active plan
              </Text>
              <Text type={summary.notFoundCount > 0 ? 'danger' : 'secondary'}>
                {summary.notFoundCount} not found
              </Text>
              <Text type="secondary">
                WEX {summary.wexCount} · MARQETA {summary.marqetaCount}
              </Text>
            </Space>

            {summary.inUseCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${summary.inUseCount} of these cards are on an active plan`}
                description="Terminating them leaves those benefits without a working card. Nothing reissues one — use Terminate and New from the Benefits tab if that is what you want."
              />
            )}

            <Space>
              <Button
                type={rowFilter === 'attention' ? 'primary' : 'default'}
                onClick={() => setRowFilter('attention')}
              >
                Needs attention ({attentionCount})
              </Button>
              <Button
                type={rowFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setRowFilter('all')}
              >
                All ({summary.totalCount})
              </Button>
              <Button
                type={rowFilter === 'orphan' ? 'primary' : 'default'}
                onClick={() => setRowFilter('orphan')}
              >
                Orphan ({summary.orphanCount})
              </Button>
            </Space>
          </>
        )}

        <Table<VcnCardBulkPreviewRow>
          rowKey={(row) => `${row.provider}:${row.vcnToken}`}
          columns={columns}
          dataSource={visibleRows}
          loading={phase === 'preview_loading'}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText:
              phase === 'results'
                ? 'No rows in this view.'
                : 'Choose a CSV to preview.',
          }}
        />
      </Space>
    </Modal>
  )
}
